import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {shareReplay, tap} from 'rxjs/operators';
import {resolveApiBase} from '../core/api-base';
import type {CaptchaConfig} from './captcha.service';

export interface TenantConfig {
  signup_enabled?: boolean;
  captcha_required?: boolean;
  // Story 54.4 — which captcha to run (present only when captcha_required).
  captcha?: CaptchaConfig;
  // Story 25.5 — server-merged login-text overrides (Environment defaults ⊕
  // Application overrides) keyed `lang -> dot-path key -> text`. Omitted when the
  // operator has set none. Applied live via TenantI18nService → Transloco.
  i18n_overrides?: Record<string, Record<string, string>>;
  // Story 29.4 — the Environment operator's default/fallback login language.
  // emjo-login applies it only when the visitor's browser language is
  // unavailable and no explicit stored choice exists (browser language still
  // wins). Omitted when the operator set no default.
  default_language?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TenantConfigService {
  private cache = new Map<string, Observable<TenantConfig>>();
  private readonly http = inject(HttpClient);

  fetchConfig(clientId: string): Observable<TenantConfig> {
    if (!clientId) {
      return of({});
    }
    const cached = this.cache.get(clientId);
    if (cached) {
      return cached;
    }
    const request$ = this.http.get<TenantConfig>(
      `${resolveApiBase()}/.well-known/openid-configuration?client_id=${encodeURIComponent(clientId)}`
    ).pipe(
      tap({ error: () => this.cache.delete(clientId) }),
      shareReplay(1)
    );
    this.cache.set(clientId, request$);
    return request$;
  }
}
